import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { submitInquiry } from "../lib/api";
import { ALL_COUNTRIES, DIAL_CODES } from "../data/countries";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { useCountryName } from "../lib/useCountryName";

// Keys are stable; only the labels are translated. The English label is what
// gets emailed, so the sales desk always reads inquiries in one language.
const PRODUCT_KEYS = [
    "rough_blocks",
    "cut_slabs",
    "polished_black",
    "black_galaxy",
    "tan_brown",
    "steel_grey",
    "imperial_red",
    "custom",
    "sample",
];

const emptyForm = (defaultProduct) => ({
    name: "",
    email: "",
    dialCode: "+91",
    phone: "",
    country: "",
    company: "",
    message: "",
    botcheck: "",
    items: [{ product: defaultProduct || PRODUCT_KEYS[0], quantity: "" }],
});

export const InquiryForm = ({ defaultProduct, compact = false }) => {
    const { t, i18n } = useTranslation();
    const countryName = useCountryName();
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState(emptyForm(defaultProduct));

    // Shown localised and alphabetised for the reader; the submitted value stays
    // the English name so the sales desk reads every inquiry in one language.
    const localisedCountries = useMemo(
        () =>
            ALL_COUNTRIES.map((c) => ({
                ...c,
                label: countryName(c.iso, c.name),
            })).sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
        [countryName, i18n.language],
    );

    // English labels for the outgoing email, regardless of the visitor's locale.
    const englishLabel = useMemo(
        () => (key) =>
            i18n.getFixedT("en")(`form.products.${key}`, { defaultValue: key }),
        [i18n],
    );

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const updateItem = (i, k) => (e) =>
        setForm((f) => ({
            ...f,
            items: f.items.map((it, idx) =>
                idx === i ? { ...it, [k]: e.target.value } : it,
            ),
        }));

    const addItem = () =>
        setForm((f) => ({
            ...f,
            items: [...f.items, { product: PRODUCT_KEYS[0], quantity: "" }],
        }));

    const removeItem = (i) =>
        setForm((f) => ({
            ...f,
            items:
                f.items.length > 1
                    ? f.items.filter((_, idx) => idx !== i)
                    : f.items,
        }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (form.botcheck) return; // honeypot
        if (!form.name || !form.email || !form.country) {
            toast.error(t("form.required_error"));
            return;
        }
        setSubmitting(true);
        try {
            const productNames = form.items
                .map((it) => englishLabel(it.product))
                .join(", ");
            const productsDetailed = form.items
                .map(
                    (it, i) =>
                        `${i + 1}. ${englishLabel(it.product)}${it.quantity ? ` - Qty: ${it.quantity}` : ""}`,
                )
                .join("\n");
            await submitInquiry({
                name: form.name,
                email: form.email,
                phone: form.phone ? `${form.dialCode} ${form.phone}` : "",
                country: form.country,
                company: form.company,
                product_interest: productNames,
                products: productsDetailed,
                message: form.message,
                language: i18n.language,
                botcheck: form.botcheck,
            });
            setDone(true);
            toast.success(t("form.success_toast"));
            setForm(emptyForm(defaultProduct));
        } catch (err) {
            toast.error(t("form.error_toast"));
        } finally {
            setSubmitting(false);
        }
    };

    const inputBase =
        "bg-transparent border-b border-[#2C2C2C] text-[#E8E3D9] py-3 focus:outline-none focus:border-[#C5A059] transition-colors placeholder:text-[#A39E93] font-light";
    const inputClass = `${inputBase} w-full`;
    const labelClass = "text-[10px] uppercase tracking-[0.3em] text-[#A39E93]";
    const optionClass = "bg-[#1A1A1A] text-[#E8E3D9]";

    return (
        <form
            onSubmit={onSubmit}
            data-testid="inquiry-form"
            className={`bg-[#1A1A1A] border border-[#2C2C2C] p-8 md:p-10 grid grid-cols-1 ${compact ? "md:grid-cols-1" : "md:grid-cols-2"} gap-x-10 gap-y-6`}
        >
            {/* Honeypot field, hidden from humans, catches bots */}
            <input
                type="text"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.botcheck}
                onChange={update("botcheck")}
                className="hidden"
            />

            <div className="flex flex-col">
                <label htmlFor="inq-name" className={labelClass}>
                    {t("form.name")} {t("form.required_star")}
                </label>
                <input
                    id="inq-name"
                    data-testid="inquiry-name"
                    className={inputClass}
                    value={form.name}
                    onChange={update("name")}
                    placeholder={t("form.name_placeholder")}
                    autoComplete="name"
                    required
                />
            </div>
            <div className="flex flex-col">
                <label htmlFor="inq-email" className={labelClass}>
                    {t("form.email")} {t("form.required_star")}
                </label>
                <input
                    id="inq-email"
                    data-testid="inquiry-email"
                    type="email"
                    className={inputClass}
                    value={form.email}
                    onChange={update("email")}
                    placeholder={t("form.email_placeholder")}
                    autoComplete="email"
                    required
                />
            </div>

            <div className="flex flex-col">
                <label htmlFor="inq-phone" className={labelClass}>
                    {t("form.phone")}
                </label>
                <div className="flex gap-2">
                    <select
                        data-testid="inquiry-dialcode"
                        aria-label={t("form.dialcode_aria")}
                        className={`${inputBase} appearance-none w-28 shrink-0`}
                        value={form.dialCode}
                        onChange={update("dialCode")}
                    >
                        {DIAL_CODES.map((c) => (
                            <option key={c.iso} value={c.dial} className={optionClass}>
                                {c.dial} {c.name}
                            </option>
                        ))}
                    </select>
                    <input
                        id="inq-phone"
                        data-testid="inquiry-phone"
                        className={`${inputBase} flex-1 min-w-0`}
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder={t("form.phone_placeholder")}
                        autoComplete="tel-national"
                    />
                </div>
            </div>
            <div className="flex flex-col">
                <label htmlFor="inq-country" className={labelClass}>
                    {t("form.country")} {t("form.required_star")}
                </label>
                <select
                    id="inq-country"
                    data-testid="inquiry-country"
                    className={`${inputClass} appearance-none`}
                    value={form.country}
                    onChange={update("country")}
                    required
                >
                    <option value="" disabled className={optionClass}>
                        {t("form.country_placeholder")}
                    </option>
                    {localisedCountries.map((c) => (
                        <option key={c.iso} value={c.name} className={optionClass}>
                            {c.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col md:col-span-2">
                <label htmlFor="inq-company" className={labelClass}>
                    {t("form.company")}
                </label>
                <input
                    id="inq-company"
                    data-testid="inquiry-company"
                    className={inputClass}
                    value={form.company}
                    onChange={update("company")}
                    placeholder={t("form.company_placeholder")}
                    autoComplete="organization"
                />
            </div>

            {/* Products of interest, multiple, each with its own quantity */}
            <div className="flex flex-col md:col-span-2 gap-3">
                <label className={labelClass}>
                    {t("form.products_label")} {t("form.required_star")}
                </label>
                {form.items.map((it, i) => (
                    <div
                        key={i}
                        data-testid={`inquiry-item-${i}`}
                        className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center"
                    >
                        <select
                            aria-label={t("form.product_aria", { n: i + 1 })}
                            data-testid={`inquiry-product-${i}`}
                            className={`${inputBase} appearance-none w-full sm:flex-1 sm:min-w-0`}
                            value={it.product}
                            onChange={updateItem(i, "product")}
                        >
                            {PRODUCT_KEYS.map((k) => (
                                <option key={k} value={k} className={optionClass}>
                                    {t(`form.products.${k}`)}
                                </option>
                            ))}
                        </select>
                        <div className="flex items-center gap-2 sm:gap-3 sm:shrink-0">
                            <input
                                aria-label={t("form.qty_aria", { n: i + 1 })}
                                data-testid={`inquiry-qty-${i}`}
                                className={`${inputBase} flex-1 sm:w-40`}
                                value={it.quantity}
                                onChange={updateItem(i, "quantity")}
                                placeholder={t("form.qty_placeholder")}
                            />
                            <button
                                type="button"
                                onClick={() => removeItem(i)}
                                aria-label={t("form.remove_aria", { n: i + 1 })}
                                disabled={form.items.length === 1}
                                className="shrink-0 p-2 text-[#A39E93] hover:text-[#C5A059] disabled:opacity-30 disabled:hover:text-[#A39E93] transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    data-testid="inquiry-add-item"
                    onClick={addItem}
                    className="self-start inline-flex items-center gap-2 text-[#C5A059] text-[11px] uppercase tracking-[0.25em] hover:gap-3 transition-all mt-1"
                >
                    <Plus size={14} /> {t("form.add_product")}
                </button>
            </div>

            <div className="flex flex-col md:col-span-2">
                <label htmlFor="inq-message" className={labelClass}>
                    {t("form.message")}
                </label>
                <textarea
                    id="inq-message"
                    data-testid="inquiry-message"
                    rows={4}
                    className={`${inputClass} resize-none`}
                    value={form.message}
                    onChange={update("message")}
                    placeholder={t("form.message_placeholder")}
                />
            </div>

            <div className="md:col-span-2 flex items-center justify-between gap-6 pt-4">
                <p className="text-xs text-[#A39E93]">
                    {done ? t("form.consent_done") : t("form.consent_default")}
                </p>
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="inquiry-submit"
                    className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0D0D0D] px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#E8E3D9] transition-colors disabled:opacity-60"
                >
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    {submitting ? t("form.sending") : t("form.send")}
                </button>
            </div>
        </form>
    );
};
